/**
 * Centralized environment configuration.
 *
 * Per RW-react-core-standards §3.4 rule 6: never read `process.env.*` inline
 * from components, pages, or utils. Every `REACT_APP_*` / `PUBLIC_URL` value
 * the UI layer needs is read once here and re-exported as a named, typed
 * constant so the rest of the app shares one stable reference.
 *
 * Auth / cross-origin env (`REACT_APP_VUE_URL`, the API base URL) is
 * intentionally owned by `config/auth.config.ts` (the AuthManager module, the
 * single owner of auth configuration) and is not duplicated here.
 */

const DEFAULT_SHOPIFY_ADMIN_URL =
  'https://admin.shopify.com/store/596584-2/orders';
const DEFAULT_CW_ORDER_PORTAL_URL = 'https://cw.restaurantware.com';
const DEFAULT_GOOGLE_AUTH_ENDPOINT = '/api/users/google-auth';

/** Public asset base path (CRA `PUBLIC_URL`); empty string in dev. */
export const PUBLIC_ASSET_BASE = process.env.PUBLIC_URL ?? '';

/** Google OAuth client id (`REACT_APP_GOOGLE_CLIENT_ID`); empty when unset. */
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';

/** Google auth endpoint path (`REACT_APP_GOOGLE_AUTH_ENDPOINT`). */
export const GOOGLE_AUTH_ENDPOINT =
  process.env.REACT_APP_GOOGLE_AUTH_ENDPOINT ?? DEFAULT_GOOGLE_AUTH_ENDPOINT;

/** Shopify admin orders base URL (`REACT_APP_SHOPIFY_ADMIN_URL`). */
export const SHOPIFY_ADMIN_URL =
  process.env.REACT_APP_SHOPIFY_ADMIN_URL ?? DEFAULT_SHOPIFY_ADMIN_URL;

/** Chefs Warehouse order-portal origin (`REACT_APP_CW_ORDER_PORTAL_URL`) —
 *  a CW staged order's Staged Order# opens `<origin>/order/<editToken>`
 *  there, with the token the ERP issues for that order. */
export const CW_ORDER_PORTAL_URL =
  process.env.REACT_APP_CW_ORDER_PORTAL_URL ?? DEFAULT_CW_ORDER_PORTAL_URL;

/** Raw API URL override (`REACT_APP_API_URL`); undefined when unset. */
export const API_URL = process.env.REACT_APP_API_URL;

/**
 * Local-only override for `rw-accounting-internal-service` calls
 * (`REACT_APP_ACCOUNTING_SERVICE_URL`), e.g. `http://localhost:8011` while
 * developing the CloseHub backend before it is published to the shared
 * gateway. Undefined when unset — callers must fall back to the shared
 * `apiClient` base URL, NOT assume this is present. Set only in a local,
 * gitignored `.env.local`.
 */
export const ACCOUNTING_SERVICE_LOCAL_OVERRIDE_URL =
  process.env.REACT_APP_ACCOUNTING_SERVICE_URL;

/**
 * Production RW ERP API origin as the Vue ERP knows it (`VITE_BASE_URL`
 * minus `/api`). Kept for the one-origin callers; `isProductionServer()`
 * checks `PRODUCTION_API_ORIGINS` below.
 */
export const PRODUCTION_RWDOMAIN_ORIGIN = 'https://erp.rwdomain.com';

/**
 * Every API origin that IS production. `utils/env.ts`'s
 * `isProductionServer()` compares the resolved API base URL against this
 * list to gate environment-specific behaviour (Clarity project id, Shopify
 * admin store slugs).
 *
 * Two entries because the two ERPs reach production through different
 * hosts: Vue through `erp.rwdomain.com/api`, this app through
 * `api.rwdomain.com` (`REACT_APP_API_URL` in the production `.env`, and the
 * hardcoded fallback in `auth.config.ts`). Matching only the Vue host made
 * the production React build read as DEVELOPMENT — every Order # link went
 * to the `rwdevelopment` Shopify store.
 */
export const PRODUCTION_API_ORIGINS: ReadonlyArray<string> = [
  PRODUCTION_RWDOMAIN_ORIGIN,
  'https://api.rwdomain.com',
];
